'use client'

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/Button'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { Database } from '@/types/database'
import { useTranslation } from 'react-i18next';

type AgeBracket = 'under_30' | '30_to_49' | '50_plus'
type Gender = 'male' | 'female' | 'unspecified'
type Persona = 'Mentor' | 'Coach' | 'Sidekick'

export default function OnboardingPage() {
  const { t } = useTranslation();

  const { user, isLoading: authLoading, updateProfile } = useAuth()
  const supabase = createClientComponentClient()
  const navigate = useNavigate()

  const [ageBracket, setAgeBracket] = useState<AgeBracket | null>(null)
  const [gender, setGender] = useState<Gender>('unspecified')
  const [persona, setPersona] = useState<Persona | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Styles
  const tileBase = 'p-4 rounded-xl border-2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500'
  const tileSelected = 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-500 shadow-sm'
  const tileUnselected = 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40'

  // Redirect unauthenticated users only after auth has finished loading
  useEffect(() => {
    if (authLoading) return
    if (!user) {
      navigate('/auth/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  // Attempt IP -> country lookup once with abort safety
  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false
    const lookup = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/', { signal: controller.signal })
        const json = await res.json()
        if (!cancelled && json?.country) {
          setDetectedCountry(String(json.country).slice(0, 2))
        }
      } catch (err: unknown) {
        if ((err as Error)?.name === 'AbortError') return
        try {
          const fallbackRes = await fetch('https://ipinfo.io/json', { signal: controller.signal })
          const fallbackJson = await fallbackRes.json()
          if (!cancelled && fallbackJson?.country) {
            setDetectedCountry(fallbackJson.country)
          }
        } catch { }
      }
    }
    lookup()
    return () => { cancelled = true; controller.abort() }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return
    if (!user) return
    if (!ageBracket || !persona) {
      setError('Please select your age bracket and preferred AI persona.')
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      console.log('Updating profile with:', {
        age_bracket: ageBracket,
        gender,
        preferred_persona: persona,
        country_code: detectedCountry ?? null
      });

      const updateData = {
        is_onboarded: true,
        age_bracket: ageBracket,
        gender,
        preferred_persona: persona,
        country_code: detectedCountry ?? null,
        preferences: {} as Database['public']['Tables']['users']['Row']['preferences']
      }

      const { error: updateError } = await supabase
        .from('users')
        .update(updateData as any)
        .eq('id', user.id)

      if (updateError) {
        console.error('Profile update error:', updateError);
        setError('Failed to save your preferences. Please try again.')
      } else {
        console.log('Profile updated successfully, navigating to root for role-based redirect');
        // Force a small delay to ensure the profile is updated
        setTimeout(() => {
          navigate('/', { replace: true })
        }, 100);
      }
    } catch (err) {
      console.error('Unexpected error updating profile:', err);
      setError('Failed to save your preferences. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl bg-white rounded-2xl border border-sage-neutral-200 p-8 shadow-sm">
        <h1 className="text-3xl font-bold text-sage-neutral-900 mb-2">{t('onboarding.set_up_your_experience')}</h1>
        <p className="text-sage-neutral-600 mb-8">{t('onboarding.answer_two_quick_steps_to_personalize')}</p>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-sage-neutral-900 mb-3">{t('onboarding.step_tell_us_bit_about_yourself')}</h2>
            <label className="block text-sage-neutral-700 mb-2">{t('onboarding.age_bracket')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label={t('onboarding.age_bracket')}>
              <button
                type="button"
                onClick={() => setAgeBracket('under_30')}
                className={`${tileBase} ${ageBracket === 'under_30' ? tileSelected : tileUnselected}`}
                role="radio" aria-checked={ageBracket === 'under_30'}
                tabIndex={0}
              >{t('onboarding.under_30')}</button>
              <button
                type="button"
                onClick={() => setAgeBracket('30_to_49')}
                className={`${tileBase} ${ageBracket === '30_to_49' ? tileSelected : tileUnselected}`}
                role="radio" aria-checked={ageBracket === '30_to_49'}
                tabIndex={0}
              >30 - 49</button>
              <button
                type="button"
                onClick={() => setAgeBracket('50_plus')}
                className={`${tileBase} ${ageBracket === '50_plus' ? tileSelected : tileUnselected}`}
                role="radio" aria-checked={ageBracket === '50_plus'}
                tabIndex={0}
              >50+</button>
            </div>

            <label className="block text-sage-neutral-700 mt-6 mb-2">Gender</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Gender">
              <button type="button" onClick={() => setGender('male')} className={`${tileBase} ${gender === 'male' ? tileSelected : tileUnselected}`} role="radio" aria-checked={gender === 'male'}>Male</button>
              <button type="button" onClick={() => setGender('female')} className={`${tileBase} ${gender === 'female' ? tileSelected : tileUnselected}`} role="radio" aria-checked={gender === 'female'}>Female</button>
              <button type="button" onClick={() => setGender('unspecified')} className={`${tileBase} ${gender === 'unspecified' ? tileSelected : tileUnselected}`} role="radio" aria-checked={gender === 'unspecified'}>{t('onboarding.prefer_not_to_say')}</button>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-sage-neutral-900 mb-3">{t('onboarding.step_how_should_your_ai_talk')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label={t('onboarding.preferred_ai_persona')}>
              <button type="button" onClick={() => setPersona('Mentor')} className={`${tileBase} text-left ${persona === 'Mentor' ? tileSelected : tileUnselected}`} role="radio" aria-checked={persona === 'Mentor'}>
                <div className="font-semibold mb-1">{t('onboarding.the_mentor')}</div>
                <div className="text-sm text-sage-neutral-600">{t('onboarding.wise_patient_and_supportive')}</div>
              </button>
              <button type="button" onClick={() => setPersona('Coach')} className={`${tileBase} text-left ${persona === 'Coach' ? tileSelected : tileUnselected}`} role="radio" aria-checked={persona === 'Coach'}>
                <div className="font-semibold mb-1">{t('onboarding.the_coach')}</div>
                <div className="text-sm text-sage-neutral-600">{t('onboarding.goal_oriented_and_encouraging')}</div>
              </button>
              <button type="button" onClick={() => setPersona('Sidekick')} className={`${tileBase} text-left ${persona === 'Sidekick' ? tileSelected : tileUnselected}`} role="radio" aria-checked={persona === 'Sidekick'}>
                <div className="font-semibold mb-1">{t('onboarding.the_sidekick')}</div>
                <div className="text-sm text-sage-neutral-600">{t('onboarding.friendly_energetic_informal')}</div>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <Button type="submit" disabled={isLoading || !ageBracket || !persona} aria-disabled={isLoading || !ageBracket || !persona} className="px-6">
              {isLoading ? <div className="flex items-center"><LoadingSpinner size="sm" className="mr-2" />{t('onboarding.saving')}</div> : 'Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}


